'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TIMING } from '../lib/constants';

interface ProcessingDotsProps {
  showSlowMessage?: boolean;
}

const VERBS = [
  'Listening...',
  'Reflecting...',
  'Considering...',
  'Processing...',
  'Thinking...',
  'Listening closely...',
  'Pondering...',
  'Thinking it through...',
  'Weighing...',
  'Contemplating...',
  'Synthesizing...',
  'Connecting dots...',
  'Sifting through...',
  'Making sense...',
  'Working through this...',
  'Mulling over...',
  'Reflecting on this...',
  'Finding threads...',
  'Piecing together...',
  'Looking more closely...',
  'Turning it over...',
  'Considering what you shared...',
  'Listening carefully...',
  'Staying with this...',
  'Making connections...',
  'Following the thread...',
  'Noticing patterns...',
  'Looking for patterns...',
  'Following what you shared...',
];

const BAR_COUNT = 5;
const VERB_INTERVAL_MS = 3000;

export function ProcessingDots({ showSlowMessage: showSlowProp }: ProcessingDotsProps) {
  const [showSlow, setShowSlow] = useState(showSlowProp ?? false);
  const [verbIndex, setVerbIndex] = useState(0);

  useEffect(() => {
    if (showSlowProp !== undefined) {
      setShowSlow(showSlowProp);
      return;
    }
    const timer = setTimeout(() => setShowSlow(true), TIMING.SLOW_PROCESSING);
    return () => clearTimeout(timer);
  }, [showSlowProp]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVerbIndex((prev) => {
        let next: number;
        do {
          next = Math.floor(Math.random() * VERBS.length);
        } while (next === prev);
        return next;
      });
    }, VERB_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center gap-5 py-12" role="status" aria-label="Processing your response">
      <div className="flex items-end justify-center gap-[3px] h-8" aria-hidden="true">
        {Array.from({ length: BAR_COUNT }, (_, i) => (
          <motion.div
            key={i}
            className="w-[3px] rounded-full bg-seafoam"
            animate={{
              height: [6, 20 + Math.random() * 12, 8, 24 + Math.random() * 8, 6],
              opacity: [0.4, 0.9, 0.5, 1, 0.4],
            }}
            transition={{
              duration: 1.3,
              repeat: Infinity,
              delay: i * 0.15,
              ease: 'easeInOut',
            }}
            style={{ height: 6 }}
          />
        ))}
      </div>

      <div className="h-6 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={verbIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3 }}
            className="font-body text-sm text-text-tertiary"
          >
            {VERBS[verbIndex]}
          </motion.p>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showSlow && (
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="font-body text-xs text-text-tertiary"
          >
            Taking a little longer than usual
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
